import React, { useMemo } from 'react';
import ComparisonTable from '../ComparisonTable';
import { parseLogicData, parseVibeData, parseUniverseData } from '../../utils/parseComparisonData';

const DecisionSection = ({ 
  sectionType,
  decision,
  sectionSummary = null,
  errorIndicators = null,
  optionAName = null,
  optionBName = null
}) => {
  if (!decision) return null;

  const sectionData = useMemo(() => {
    let parsed;
    
    if (sectionType === 'logic') {
      parsed = parseLogicData(decision.logic_pros || [], decision.logic_cons || []);
      return {
        category: 'Logic',
        explanation: 'The practical considerations. Facts, consequences, and rational thinking.',
        parsed
      };
    } else if (sectionType === 'universe') {
      parsed = parseUniverseData(decision.serendipity_input || '');
      
      // Smart swap: If Universe optionA matches provided optionBName, swap them
      if (optionAName && optionBName && parsed.optionA && parsed.optionB) {
        const universeAName = parsed.optionA.name.toLowerCase();
        const universeBName = parsed.optionB.name.toLowerCase();
        const targetAName = optionAName.toLowerCase();
        const targetBName = optionBName.toLowerCase();
        
        // Check if they're swapped (Universe A matches target B)
        const aMatchesB = universeAName.includes(targetBName.split(' ')[0]) || targetBName.includes(universeAName.split(' ')[0]);
        const bMatchesA = universeBName.includes(targetAName.split(' ')[0]) || targetAName.includes(universeBName.split(' ')[0]);
        
        if (aMatchesB || bMatchesA) {
          // Swap
          const temp = { ...parsed.optionA };
          parsed.optionA = { ...parsed.optionB };
          parsed.optionB = temp;
        }
      }
      
      return {
        category: 'Universe',
        explanation: 'What you notice in the world. Signs, synchronicities, and meaningful coincidences.',
        parsed
      };
    } else if (sectionType === 'vibe') {
      parsed = parseVibeData(decision.vibe_input || '');
      
      // Smart swap: If Vibe optionA matches provided optionBName, swap them
      if (optionAName && optionBName && parsed.optionA && parsed.optionB) {
        const vibeAName = parsed.optionA.name.toLowerCase();
        const vibeBName = parsed.optionB.name.toLowerCase();
        const targetAName = optionAName.toLowerCase();
        const targetBName = optionBName.toLowerCase();
        
        const aMatchesB = vibeAName.includes(targetBName.split(' ')[0]) || targetBName.includes(vibeAName.split(' ')[0]);
        const bMatchesA = vibeBName.includes(targetAName.split(' ')[0]) || targetAName.includes(vibeBName.split(' ')[0]);
        
        if (aMatchesB || bMatchesA) {
          const temp = { ...parsed.optionA };
          parsed.optionA = { ...parsed.optionB };
          parsed.optionB = temp;
        }
      }
      
      return {
        category: 'Vibe',
        explanation: "Your body's wisdom. How does each option feel?",
        parsed
      };
    }
    return null;
  }, [sectionType, decision, optionAName, optionBName]);

  if (!sectionData) return null;

  // Apply name overrides if provided (for Universe/Vibe to match Logic names)
  if (optionAName && sectionData.parsed.optionA) {
    sectionData.parsed.optionA.name = optionAName;
  }
  if (optionBName && sectionData.parsed.optionB) {
    sectionData.parsed.optionB.name = optionBName;
  }

  return (
    <div className="decision-section-wrapper">
      <div className="mb-2 flex items-center gap-1" style={{ minHeight: '28px' }}>
        {errorIndicators && (
          <>
            <span className="text-xs text-gray-500">Issues:</span>
            {errorIndicators}
          </>
        )}
      </div>
      
      <div className="glass-card p-6 mb-8">
        <ComparisonTable
          category={sectionData.category}
          explanation={sectionData.explanation}
          optionA={sectionData.parsed.optionA.name}
          optionB={sectionData.parsed.optionB.name}
          itemsA={sectionData.parsed.optionA.items}
          itemsB={sectionData.parsed.optionB.items}
          colorA="turquoise"
          colorB="pink"
          iconA={decision.option_a_icon}
          iconB={decision.option_b_icon}
        />
        
        {sectionSummary && (
          <div className="mt-6 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
            <p className="text-gray-800 leading-relaxed">
              {sectionSummary.split('**').map((part, idx) => 
                idx % 2 === 1 ? <strong key={idx} className="font-bold text-gray-900">{part}</strong> : part
              )}
            </p>
          </div>
        )}
        {!sectionSummary && sectionType === 'vibe' && (
          <div className="mt-6 p-2 bg-yellow-50 text-xs text-gray-500">
            DEBUG: No vibe summary provided
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionSection;
